import React from 'react';
import Icon from '../../../components/AppIcon';

const FilterPanel = ({ filters, onFilterChange }) => {
  const handleInputChange = (field, value) => {
    onFilterChange({ [field]: value });
  };

  const handleClearFilters = () => {
    onFilterChange({
      dateRange: 'last7days',
      startDate: '',
      endDate: '',
      userType: 'all',
      activityCategory: 'all',
      severityLevel: 'all',
      searchQuery: ''
    });
  };

  const hasActiveFilters = 
    filters?.dateRange !== 'last7days' ||
    filters?.userType !== 'all' ||
    filters?.activityCategory !== 'all' ||
    filters?.severityLevel !== 'all' ||
    filters?.searchQuery !== '';

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <h2 className="text-lg font-semibold text-foreground font-heading flex items-center gap-2">
          <Icon name="Filter" size={20} className="text-primary" />
          Filter Activity Logs
        </h2>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-md hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={14} />
            Clear Filters
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Search Query */}
        <div className="lg:col-span-3">
          <label className="block text-sm font-medium text-foreground font-body mb-2">
            Search
          </label>
          <div className="relative">
            <Icon 
              name="Search" 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
            />
            <input
              type="text"
              value={filters?.searchQuery}
              onChange={(e) => handleInputChange('searchQuery', e?.target?.value)}
              placeholder="Search by client name, user, or action..."
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
            />
          </div>
        </div>

        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-foreground font-body mb-2">
            Date Range
          </label>
          <select
            value={filters?.dateRange}
            onChange={(e) => handleInputChange('dateRange', e?.target?.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7days">Last 7 Days</option>
            <option value="last30days">Last 30 Days</option>
            <option value="last90days">Last 90 Days</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        {/* User Type */}
        <div>
          <label className="block text-sm font-medium text-foreground font-body mb-2">
            User Type
          </label>
          <select
            value={filters?.userType}
            onChange={(e) => handleInputChange('userType', e?.target?.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
          >
            <option value="all">All Users</option>
            <option value="free">Free Plan</option>
            <option value="growth">Growth Plan</option>
            <option value="scale">Scale Plan</option>
            <option value="enterprise">Enterprise Plan</option>
          </select>
        </div>

        {/* Activity Category */}
        <div>
          <label className="block text-sm font-medium text-foreground font-body mb-2">
            Activity Category
          </label>
          <select
            value={filters?.activityCategory}
            onChange={(e) => handleInputChange('activityCategory', e?.target?.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
          >
            <option value="all">All Activities</option>
            <option value="authentication">Login/Authentication</option>
            <option value="subscription">Subscription Changes</option>
            <option value="payment">Payment Events</option>
            <option value="administrative">Administrative Actions</option>
            <option value="bmc">BMC Reports</option>
            <option value="predictive">Predictive Analysis</option>
            <option value="goal">Goal Planning</option>
            <option value="reports">Report Downloads</option>
            <option value="api">API Usage</option>
          </select>
        </div>

        {/* Severity Level */}
        <div>
          <label className="block text-sm font-medium text-foreground font-body mb-2">
            Severity Level
          </label>
          <select
            value={filters?.severityLevel}
            onChange={(e) => handleInputChange('severityLevel', e?.target?.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
          >
            <option value="all">All Levels</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        {/* Custom Date Range (shown when dateRange is 'custom') */}
        {filters?.dateRange === 'custom' && (
          <>
            <div>
              <label className="block text-sm font-medium text-foreground font-body mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={filters?.startDate}
                onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground font-body mb-2">
                End Date
              </label>
              <input
                type="date"
                value={filters?.endDate}
                onChange={(e) => handleInputChange('endDate', e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
              />
            </div>
          </>
        )}
      </div>
      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground font-caption">Active Filters:</span>
            {filters?.searchQuery && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                Search: {filters?.searchQuery}
                <button
                  onClick={() => handleInputChange('searchQuery', '')}
                  className="hover:bg-primary/20 rounded-full p-0.5 transition-smooth"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            {filters?.userType !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                User: {filters?.userType}
                <button
                  onClick={() => handleInputChange('userType', 'all')}
                  className="hover:bg-primary/20 rounded-full p-0.5 transition-smooth"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            {filters?.activityCategory !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                Category: {filters?.activityCategory}
                <button
                  onClick={() => handleInputChange('activityCategory', 'all')}
                  className="hover:bg-primary/20 rounded-full p-0.5 transition-smooth"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            {filters?.severityLevel !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                Severity: {filters?.severityLevel}
                <button
                  onClick={() => handleInputChange('severityLevel', 'all')}
                  className="hover:bg-primary/20 rounded-full p-0.5 transition-smooth"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;